import api from './apiClient';
import { claimSpeechPlayback, registerSpeechPlayback, releaseSpeechPlayback } from './SpeechPlaybackCoordinator';
import { getListeningAudio, unlockListeningAudio } from './ListeningAudioTransport';

let sequence = 0;
export default class ReadingAudioPlayer {
  constructor(cardId, { audioFactory = getListeningAudio, client = api, gapMs = 350 } = {}) {
    this.cardId = cardId; this.client = client; this.audioFactory = audioFactory; this.gapMs = gapMs;
    this.unlockAudio = audioFactory === getListeningAudio ? unlockListeningAudio : () => Promise.resolve(true);
    this.owner = `reading-audio-${++sequence}`; this.listeners = new Set(); this.cache = new Map();
    this.controller = new AbortController(); this.token = 0; this.eligible = true;
    this.state = { status: 'idle', units: [], index: -1, currentTime: 0, duration: 0, error: null, activeId: null };
    this.unregister = registerSpeechPlayback(this.owner, () => this.stop());
  }
  subscribe = listener => { this.listeners.add(listener); return () => this.listeners.delete(listener); };
  emit(patch) { this.state = { ...this.state, ...patch }; this.listeners.forEach(listener => listener(this.state)); }
  ownsSource() { return Boolean(this.playbackSource && this.element?.src === this.playbackSource); }
  audio() {
    if (!this.element) {
      this.element = this.audioFactory(); this.element.preload = 'auto';
      const handlers = {
        timeupdate: () => this.emit({ currentTime: this.element.currentTime || 0 }),
        loadedmetadata: () => this.emit({ duration: Number.isFinite(this.element.duration) ? this.element.duration : 0 }),
        seeking: () => { if (['playing', 'paused'].includes(this.state.status)) this.eligible = false; },
        playing: () => { if (this.state.status !== 'idle') this.emit({ status: 'playing', activeId: this.state.units[this.state.index]?.id }); },
        ended: () => this.ended(),
        error: () => { if (this.state.status !== 'idle') this.emit({ status: 'error', error: 'audio' }); },
      };
      this.audioListeners = Object.entries(handlers).map(([name, handler]) => {
        const guarded = () => { if (this.ownsSource()) handler(); };
        this.element.addEventListener(name, guarded);
        return [name, guarded];
      });
    }
    return this.element;
  }
  async load() {
    if (!this.playlistPromise) this.playlistPromise = this.client.getReadingPlaylist(this.cardId, { signal: this.controller.signal })
      .then(result => {
        // Also tolerate an older API/cached playlist which still includes "...".
        const silent = unit => typeof unit.text === 'string' && !/[\p{L}\p{N}]/u.test(unit.text);
        const units = result.units.filter(unit => !silent(unit));
        this.silentUnits = [...(result.silentUnits || []), ...result.units.filter(silent)];
        this.revision = result.revision; this.emit({ units });
        return { ...result, units, silentUnits: this.silentUnits };
      })
      .catch(error => { this.playlistPromise = null; throw error; });
    return this.playlistPromise;
  }
  async prepare(index) {
    const unit = this.state.units[index];
    if (!unit) throw new Error('Empty reading playlist');
    if (!this.cache.has(unit.clipKey)) {
      const promise = (async () => {
        let audio = unit.audio;
        if (unit.status !== 'ready') ({ audio } = await this.client.generateReadingUnit(this.cardId, unit.id, this.revision, { signal: this.controller.signal }));
        let data;
        try { data = await this.client.fetchTtsAudioBlob(audio.url, { signal: this.controller.signal }); }
        catch (error) {
          if (error.status !== 404) throw error;
          ({ audio } = await this.client.generateReadingUnit(this.cardId, unit.id, this.revision, { signal: this.controller.signal }));
          data = await this.client.fetchTtsAudioBlob(audio.url, { signal: this.controller.signal });
        }
        if (this.controller.signal.aborted) throw Object.assign(new Error('Stopped'), { name: 'AbortError' });
        return URL.createObjectURL(data);
      })().catch(error => { this.cache.delete(unit.clipKey); throw error; });
      this.cache.set(unit.clipKey, promise);
    }
    return this.cache.get(unit.clipKey);
  }
  async start(index, mode = this.mode || 'page') {
    const token = ++this.token;
    clearTimeout(this.gap); this.mode = mode; this.waitingNext = false; this.retryOnResume = false;
    if (this.ownsSource()) this.element.pause();
    this.playbackSource = null;
    claimSpeechPlayback(this.owner);
    // Invoke synchronously, before fetching the playlist/MP3 loses the tap.
    void this.unlockAudio();
    this.audio();
    this.emit({ status: 'loading', index, currentTime: 0, duration: 0, activeId: null, error: null });
    try {
      await this.load();
      if (token !== this.token) return;
      if (!this.state.units.length) {
        this.emit({ status: 'ended', index: -1, activeId: null });
        releaseSpeechPlayback(this.owner);
        this.finish({ finished: true, eligible: false });
        return;
      }
      const url = await this.prepare(index);
      if (token !== this.token) return;
      this.playbackSource = url; this.element.src = url; this.element.currentTime = 0;
      await this.element.play();
      if (token !== this.token) return;
      if (mode === 'page') [index + 1, index + 2].filter(next => next < this.state.units.length)
        .forEach(next => { void this.prepare(next).catch(() => {}); });
    } catch (error) {
      if (token !== this.token || error.name === 'AbortError') return;
      if (error.status === 409) { this.playlistPromise = null; this.eligible = false; }
      this.emit({ status: error.name === 'NotAllowedError' ? 'blocked' : 'error', error: error.status === 409 ? 'changed' : 'audio' });
    }
  }
  playPage() {
    this.finish(false); this.eligible = true;
    const promise = new Promise(resolve => { this.resolve = resolve; });
    void this.start(0, 'page'); return promise;
  }
  async playUnit(position) {
    this.finish(false); this.mode = 'single'; clearTimeout(this.gap);
    if (this.ownsSource()) this.element.pause();
    this.playbackSource = null;
    claimSpeechPlayback(this.owner);
    void this.unlockAudio();
    this.waitingNext = false; this.retryOnResume = false;
    this.emit({ status: 'loading', activeId: null });
    const token = ++this.token;
    try {
      await this.load();
      if (token !== this.token) return;
      const matches = unit => typeof position === 'string' ? unit.id === position
        : unit.segmentIndex === position.segmentIndex && unit.sentenceIndex === (position.sentenceIndex || 0);
      const index = this.state.units.findIndex(matches);
      if (index >= 0) void this.start(index, 'single');
      else if (this.silentUnits?.some(matches)) this.stop();
      else this.emit({ status: 'error', error: 'audio' });
    } catch (error) { if (token === this.token && error.name !== 'AbortError') this.emit({ status: 'error', error: 'audio' }); }
  }
  pause() {
    clearTimeout(this.gap);
    if (this.state.status === 'loading') { ++this.token; this.retryOnResume = true; }
    this.element?.pause(); this.emit({ status: 'paused' });
  }
  async resume() {
    if (this.retryOnResume || ['error', 'loading'].includes(this.state.status) || !this.element?.src) {
      this.retryOnResume = false; return this.start(Math.max(0, this.state.index));
    }
    if (this.waitingNext) { this.waitingNext = false; return this.start(this.state.index + 1); }
    claimSpeechPlayback(this.owner);
    try { await this.element.play(); } catch (_) { this.emit({ status: 'blocked', error: 'audio' }); }
  }
  toggle() {
    if (['playing', 'loading', 'gap'].includes(this.state.status)) this.pause();
    else if (['paused', 'blocked', 'error'].includes(this.state.status)) void this.resume();
    else void this.playPage();
  }
  move(delta) {
    const index = Math.max(0, Math.min(this.state.units.length - 1, this.state.index + delta));
    this.eligible = false; this.waitingNext = false; this.retryOnResume = false;
    void this.start(index);
  }
  seek(seconds) { if (!this.element || !Number.isFinite(seconds)) return; this.eligible = false; this.element.currentTime = seconds; this.emit({ currentTime: seconds }); }
  ended() {
    if (this.state.status !== 'playing' || this.element?.ended === false) return;
    if (this.mode === 'page' && this.state.index < this.state.units.length - 1) {
      this.waitingNext = true; this.emit({ status: 'gap', activeId: null });
      this.gap = setTimeout(() => { this.waitingNext = false; void this.start(this.state.index + 1); }, this.gapMs);
    } else {
      this.emit({ status: 'ended', activeId: null }); releaseSpeechPlayback(this.owner);
      this.finish({ finished: true, eligible: this.mode === 'page' && this.eligible });
    }
  }
  finish(result) { const resolve = this.resolve; this.resolve = null; resolve?.(result); }
  stop() {
    ++this.token; clearTimeout(this.gap);
    if (this.ownsSource()) this.element.pause();
    this.playbackSource = null; this.waitingNext = false; this.retryOnResume = false;
    this.finish(false); this.emit({ status: 'idle', activeId: null }); releaseSpeechPlayback(this.owner);
  }
  dispose() {
    const ownedSource = this.ownsSource();
    this.stop(); this.controller.abort(); this.unregister();
    this.audioListeners?.forEach(([name, listener]) => this.element.removeEventListener(name, listener));
    this.cache.forEach(promise => { promise.then(url => URL.revokeObjectURL(url)).catch(() => {}); }); this.cache.clear();
    if (ownedSource) { this.element.removeAttribute('src'); this.element.load?.(); }
    this.listeners.clear();
  }
}
